import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  Component,
  Inject,
  InjectionToken,
  Input,
  OnDestroy,
  ViewContainerRef
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subject, switchMap } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  startWith,
  take,
  takeUntil
} from 'rxjs/operators';
import { RetailProduct } from '../../../models/retail-product';
import { BarcodeScannerDialogComponent } from '../../barcode-scanner-dialog/dialog/dialog.component';
import { TestIdDirective } from '../../directives/test-id.directive';
import { extractQuantityAndQuery } from '../../helpers/extractQuantity';
import { RetailProductService } from '../../services/retail-product.service';
import { EditRetailItemComponent } from '../edit-retail-item/edit-retail-item.component';
import { RetailItemPersistence } from './retail-item-persistence.interface';
import { RetailItemPersistenceService } from './retail-item-persistence.service';
import { CategoryGridDialogComponent } from '../../item-search/category-grid-item-selection/category-grid-dialog/category-grid-dialog.component';

export const RETAIL_ITEM_PERSISTANCE_SERVICE =
  new InjectionToken<RetailItemPersistence>('RETAIL_ITEM_PERSISTANCE_SERVICE');

@Component({
  selector: 'app-retail-item-search',
  templateUrl: './retail-item-search.component.html',
  providers: [RetailProductService],
  standalone: true,
  imports: [
    NgIf,
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    NgFor,
    MatOption,
    TestIdDirective,
    AsyncPipe,
    MatIcon,
    MatIconButton,
    NgClass
  ],
  styles: [
    `
      :host {
        flex-basis: 100%;
      }
    `
  ]
})
export class RetailItemSearchComponent implements OnDestroy {
  @Input() canAdd = false;
  @Input() parentType: string;
  @Input() addFreetext = true;
  @Input() eposMode = false;
  @Input() showGridButton = false;
  viewContainerRef: ViewContainerRef;
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.searchInput.disable();
    } else {
      this.searchInput.enable();
    }
  }

  _disabled = false;
  searchInput = new FormControl<string>('');
  searchResults: Observable<RetailProduct[]>;
  private ngUnsubscribe = new Subject<void>();
  FREE_TEXT_ITEM = {};

  constructor(
    private retailProductService: RetailProductService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private vcr: ViewContainerRef,
    @Inject(RETAIL_ITEM_PERSISTANCE_SERVICE)
    private persistenceService: RetailItemPersistenceService
  ) {
    this.searchResults = this.searchInput.valueChanges.pipe(
      debounceTime(300),
      switchMap((query) =>
        query
          ? this.retailProductService
              .search(encodeURIComponent(query))
              .pipe(catchError(() => of([])))
          : of([])
      ),
      startWith([])
    );
    this.addFromUrl();
  }

  addFromUrl() {
    if (this.activatedRoute.snapshot.queryParams['addRetailItemId']) {
      this.retailProductService
        .get(this.activatedRoute.snapshot.queryParams['addRetailItemId'])
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((item) => {
          this.add(item);
          this.router.navigate([], {
            queryParams: { addRetailItemId: undefined },
            queryParamsHandling: 'merge'
          });
        });
    }
  }

  add(item: RetailProduct) {
    this.persistenceService.add(item);
  }

  openScanner() {
    this.dialog
      .open(BarcodeScannerDialogComponent, {
        data: {
          type: 'retailProduct'
        },
        viewContainerRef: this.vcr
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.ngUnsubscribe), filter(Boolean))
      .subscribe((result) => {
        this.add(result.resource);
      });
  }

  attemptQuickAdd() {
    const search = this.searchInput.value;
    this.searchInput.setValue('');
    if (typeof search !== 'string' || search === '') {
      return;
    }
    const { quantity, query } = extractQuantityAndQuery(search);
    const quickAddArgs = { quantity };
    if (query && query.indexOf('b:') === 0) {
      quickAddArgs['barcode'] = query.replace('b:', '');
    } else {
      quickAddArgs['search'] = query;
    }
    this.persistenceService.attemptQuickAdd(quickAddArgs);
  }

  addFreeTextItem() {
    this.dialog.open(EditRetailItemComponent, {
      data: {},
      viewContainerRef: this.vcr
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleOptionSelected({ option: { value } }: MatAutocompleteSelectedEvent) {
    if (value) {
      if (value === this.FREE_TEXT_ITEM) {
        this.addFreeTextItem();
        return;
      }
      this.add(value);
    }
  }

  openGrid() {
    this.dialog
      .open(CategoryGridDialogComponent, {
        minWidth: '80vw',
        height: '80vh',
        viewContainerRef: this.viewContainerRef,
        data: {
          productType: 'retail'
        }
      })
      .afterClosed()
      .pipe(filter((result) => Boolean(result)))
      .subscribe((result) => {
        this.persistenceService.attemptQuickAdd({
          retail_product_id: result.id,
          quantity: 1
        });
      });
  }

  emptyDisplay() {
    return '';
  }
}
